import { useEffect, useRef } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'

import { CurrencyInput } from '@/components/ui/currency-input'
import { NumericInput } from '@/components/ui/numeric-input'

import {
  formatServiceFee,
  MAX_PERCENTAGE_FEE,
  SERVICE_FEE_SYMBOLS,
  TrainerFormData,
} from './trainer-form-modal.constants'

interface ServiceFeeFieldProps {
  index: number
}

export const ServiceFeeField = ({ index }: ServiceFeeFieldProps) => {
  const { control, getValues } = useFormContext<TrainerFormData>()
  const type = useWatch({ control, name: `services.${index}.type` })
  const { field, fieldState } = useController({ control, name: `services.${index}.fee` })

  const { onChange: onFeeChange, value: fee } = field
  const previousType = useRef(type)

  // Note: only reacts to the fee type being switched (e.g. 150.00 -> 100 as a percentage) - reformatting
  // on every keystroke would make it impossible to type a decimal
  useEffect(() => {
    if (previousType.current === type) return
    previousType.current = type
    onFeeChange(formatServiceFee(getValues(`services.${index}.fee`), type))
  }, [type, index, getValues, onFeeChange])

  const handleBlur = () => {
    field.onBlur()
    onFeeChange(formatServiceFee(fee, type))
  }

  const commonProps = {
    error: fieldState.error?.message,
    min: 0,
    name: field.name,
    placeholder: formatServiceFee('0', type),
    size: 'medium',
    value: fee ?? '',
    onBlur: handleBlur,
    onChange: onFeeChange,
  } as const

  if (type === 'rates') return <CurrencyInput {...commonProps} />

  return (
    <NumericInput
      {...commonProps}
      decimalPlaces={2}
      leadingSlot={SERVICE_FEE_SYMBOLS[type]}
      max={MAX_PERCENTAGE_FEE}
      mode="decimal"
    />
  )
}
